import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { openedFromRecoveryLink, supabase } from "./supabase";

interface AuthCtx {
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  /** Session ouverte par un lien « mot de passe oublié » : un nouveau mot de passe est attendu. */
  recovery: boolean;
  endRecovery: () => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx>({ session: null, loading: false, isAdmin: false, recovery: false, endRecovery: () => {}, signOut: async () => {} });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(Boolean(supabase));
  const [isAdmin, setIsAdmin] = useState(false);
  const [recovery, setRecovery] = useState(openedFromRecoveryLink);

  useEffect(() => {
    if (!supabase) return;
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    const { data } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (event === "PASSWORD_RECOVERY") setRecovery(true);
      if (event === "SIGNED_OUT") setRecovery(false);
    });
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!supabase || !session) {
      setIsAdmin(false);
      return;
    }
    // RLS : un utilisateur ne peut lire que sa propre ligne dans admin_users
    supabase.from("admin_users").select("user_id").eq("user_id", session.user.id).maybeSingle()
      .then(({ data }) => setIsAdmin(Boolean(data)));
  }, [session]);

  const signOut = async () => {
    await supabase?.auth.signOut();
  };

  return <Ctx.Provider value={{ session, loading, isAdmin, recovery, endRecovery: () => setRecovery(false), signOut }}>{children}</Ctx.Provider>;
}

export const useAuth = () => useContext(Ctx);
